const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === "[object Object]"

/**
 * Deep-cleans an ad form value before it is written to Firestore:
 * - empty/whitespace-only strings are dropped (so "optional" fields the editor
 *   left blank disappear and the consumer naturally hides them)
 * - strings are trimmed
 * - non-finite numbers are dropped
 * - nested objects/arrays are cleaned recursively and pruned when they end up empty
 * - explicit booleans (e.g. `disabled: false`) and finite numbers are preserved
 */
const cleanAdValue = (value: unknown): unknown => {
  if (value === undefined || value === null) return undefined
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined
  if (typeof value === "boolean") return value
  if (Array.isArray(value)) {
    const cleanedArray = value.map(cleanAdValue).filter((item) => item !== undefined)
    return cleanedArray.length ? cleanedArray : undefined
  }
  if (isPlainObject(value)) {
    const cleaned: Record<string, unknown> = {}
    Object.entries(value).forEach(([key, nested]) => {
      const cleanedNested = cleanAdValue(nested)
      if (cleanedNested !== undefined) cleaned[key] = cleanedNested
    })
    return Object.keys(cleaned).length ? cleaned : undefined
  }
  return value
}

/**
 * Normalizes an ad's form data prior to save, stripping empty optional fields
 * (link, overlayText, location, expirationDate, ...) while keeping explicit
 * booleans. Mirrors `normalizeTicketDesignForSave` in raffleFormUtils.ts.
 */
export const normalizeAdForSave = <T extends Record<string, unknown>>(formData: T): T => {
  if (!isPlainObject(formData)) return formData
  const cleaned = cleanAdValue(formData)
  return (isPlainObject(cleaned) ? cleaned : {}) as T
}
